import {
    CloudStorageType,
    FilterObject,
    GeometryCollectionType,
    MapTheme,
    useGenerateFileMutation,
} from "@biggeo/bg-server-lib/studio";
import {
    Button,
    Checkbox,
    Divider,
    FlexScrollArea,
    Grid,
    MultilineTextField,
    Stack,
    thumbnailAvatarClasses,
    ThumbnailAvatar,
    TextField,
    Typography,
    AvatarGroup,
    IconAvatar,
    Tag,
} from "@biggeo/bg-ui/lab";
import { Formik } from "formik";
import { useState } from "react";
import Zod from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import {
    useErrorToast,
    useSuccessToast,
} from "../../../toaster/containers/redux/hooks";
import { IGenerateFileContainer } from "../containers/GenerateFileContainer";
import isEmpty from "lodash/isEmpty";
import { MapContextDataset, MapContextPolygon } from "../context/types";
import {
    convertFilterObjectToInputType,
    convertToParquetFileName,
} from "../utils/filtered-data-hooks-utils";
import { AddOutline } from "@biggeo/bg-ui/lab/icons";
import { DataManagementAddandIndexLayout } from "../../../common/layouts/DataManagementAddandIndexLayout";
import DatascapeMainLayout from "../../../common/layouts/DatascapeMainLayout";
import { logoMap } from "@biggeo/bg-utils";
import { UploadOutline } from "@biggeo/bg-ui/icons";
import { match } from "ts-pattern";
import { getInputGeometry } from "../../utils/geometry-utils";
import { getSavedViewInputDatasets } from "../../filter-criteria/utils/utils";
import { getInputPolygon } from "../context/utils/map-utils";

interface IGenerateFile
    extends Pick<
        IGenerateFileContainer,
        "bounds" | "viewport" | "setOpenGenerateFilePage" | "datasets"
    > {
    readonly mapTemplateId: string;
    readonly polygons: MapContextPolygon[];
    readonly style?: MapTheme;
    readonly multiFilters: FilterObject[];
    readonly selectedDatasets: (MapContextDataset & {
        cloudStorageType?: CloudStorageType;
    })[];
}

interface IGeneratedFileForm {
    name: string;
    description?: string;
    generateFiles: Array<{
        fkDataSourceId: string;
        originOrgId: string;
        name: string;
        dataSourceName: string;
        selected: boolean;
    }>;
}

const GenerateFile = ({
    bounds,
    viewport,
    setOpenGenerateFilePage,
    polygons,
    mapTemplateId,
    style,
    datasets,
    selectedDatasets,
    multiFilters,
}: IGenerateFile) => {
    const errorToast = useErrorToast();
    const successToast = useSuccessToast();
    const [showDes, setShowDes] = useState(false);
    const {
        executeMutation: generateFile,
        mutationReturn: [_, { loading: gLoading }],
    } = useGenerateFileMutation();

    const onClose = () => {
        window.location.reload();
    };

    const savedViewDatasets = getSavedViewInputDatasets({ datasets });

    const savedViewport = bounds
        ? {
              latBounds: {
                  min: bounds._sw.lat,
                  max: bounds._ne.lat,
              },
              lngBounds: {
                  min: bounds._sw.lng,
                  max: bounds._ne.lng,
              },
          }
        : viewport;

    const geometries = polygons.map((p) => getInputGeometry(p.feature));

    const handleGenerateFile = async (values: IGeneratedFileForm) => {
        generateFile({
            variables: {
                input: {
                    name: values.name,
                    description: values.description,
                    inputCreateSavedView: {
                        name: `Saved view for ${values.name}`,
                        description: `Saved view for ${values.name} file generation`,
                        viewport: savedViewport,
                        fkMapTemplateId: mapTemplateId,
                        datasets: savedViewDatasets,
                        geometries: isEmpty(geometries)
                            ? undefined
                            : {
                                  type: GeometryCollectionType.GeometryCollection,
                                  geometries,
                              },
                        img: "no screenshot",
                        theme: style || MapTheme.light,
                        isExport: true,
                    },
                    inputGeneratedFiles: values.generateFiles
                        .filter((f) => f.selected)
                        .map((file) => ({
                            fkDataSourceId: file.fkDataSourceId,
                            name: convertToParquetFileName(file.name),
                            originOrgId: file.originOrgId,
                        })),
                    multipolygon: polygons.map((d) => {
                        const polygon = getInputPolygon(d.feature);

                        return {
                            inners: polygon.inners,
                            outer: polygon.outer,
                        };
                    }),
                    viewport: savedViewport,
                    filters: multiFilters.map(convertFilterObjectToInputType),
                },
            },
            onError: (err) =>
                errorToast(`Failed to generate files: ${err.message}`),
            onCompleted: () => {
                successToast("File generation request sent successfully.");
                onClose();
            },
        });
    };

    const initialValues = {
        name: `Points of ${datasets[0]?.dataSource.name}`,
        description: "",
        generateFiles:
            selectedDatasets.map((d) => ({
                fkDataSourceId: d.dataSource.id,
                originOrgId: d.dataSource.fkOrganizationId,
                dataSourceName: `Points of ${d.dataSource.name}`,
                name: `Points of ${d.dataSource.name}`,
                selected: true,
            })) || [],
    };

    return (
        <DatascapeMainLayout hideSidebar>
            <FlexScrollArea>
                <Formik<IGeneratedFileForm>
                    initialValues={initialValues}
                    onSubmit={(values) => {
                        handleGenerateFile(values);
                    }}
                    validationSchema={toFormikValidationSchema(
                        Zod.object({
                            name: Zod.string(),
                            description: Zod.string().optional(),
                            generateFiles: Zod.array(
                                Zod.object({
                                    fkDataSourceId: Zod.string(),
                                    name: Zod.string().refine(
                                        (value) => !/[\/\\:*?"<>';!@#$%^&\[\]{}+~`|]/.test(value),
                                        { message: "Your file name has invalid character" }
                                    ),
                                    selected: Zod.boolean(),
                                })
                            ).refine((files) => files.some((f) => f.selected), {
                                message:
                                    "Select at least one dataset to generate file",
                            }),
                        })
                    )}
                    validateOnMount
                >
                    {({ values, setValues, handleSubmit, isValid, errors, validateField }) => {
                        const onChange = (i: Partial<IGeneratedFileForm>) => {
                            setValues((p) => ({ ...p, ...i }));
                        };
                        return (
                            <Stack
                                gap={2}
                                sx={{
                                    backgroundColor: (theme) =>
                                        theme.palette.background.main,
                                    width: "100%",
                                    height: "100%",
                                }}
                            >
                                <Stack
                                    sx={{
                                        maxWidth: (theme) => theme.spacing(245),
                                        height: "100%",
                                        display: "flex",
                                        alignSelf: "center",
                                    }}
                                >
                                    <DataManagementAddandIndexLayout
                                        disabled={gLoading || !isValid}
                                        content={
                                            <Stack gap={4} sx={{ paddingY: 4 }}>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="bold"
                                                >
                                                    Generate New Files
                                                </Typography>
                                                <Divider />
                                                <Grid container>
                                                    <Grid item xs={12} md={4}>
                                                        <Typography
                                                            variant="body3"
                                                            fontWeight="bold"
                                                        >
                                                            Details
                                                        </Typography>
                                                        <Typography
                                                            variant="body3"
                                                            sx={{
                                                                color: (
                                                                    theme
                                                                ) =>
                                                                    theme
                                                                        .palette
                                                                        .disabled
                                                                        .onContainer,
                                                            }}
                                                        >
                                                            Give your dataset a
                                                            clear and
                                                            recognizable name
                                                            for easier
                                                            management and
                                                            discovery.
                                                        </Typography>
                                                    </Grid>
                                                    <Grid
                                                        item
                                                        container
                                                        xs={12}
                                                        md={8}
                                                        gap={2}
                                                    >
                                                        <TextField
                                                            label="Name"
                                                            fullWidth
                                                            required
                                                            value={
                                                                values.name ||
                                                                ""
                                                            }
                                                            onChange={(_, v) =>
                                                                onChange({
                                                                    name:
                                                                        v || "",
                                                                })
                                                            }
                                                        />
                                                        {showDes ? (
                                                            <MultilineTextField
                                                                label="Description"
                                                                subLabel="(Optional)"
                                                                fullWidth
                                                                value={
                                                                    values.description ||
                                                                    ""
                                                                }
                                                                onChange={(
                                                                    _,
                                                                    v
                                                                ) =>
                                                                    onChange({
                                                                        description:
                                                                            v,
                                                                    })
                                                                }
                                                            />
                                                        ) : (
                                                            <Button
                                                                startNode={
                                                                    <AddOutline size="xs" />
                                                                }
                                                                variant="minimal"
                                                                onClick={() =>
                                                                    setShowDes(
                                                                        true
                                                                    )
                                                                }
                                                            >
                                                                Add description
                                                            </Button>
                                                        )}
                                                    </Grid>
                                                </Grid>
                                                <Divider />
                                                <Grid container>
                                                    <Grid item xs={4}>
                                                        <Typography
                                                            variant="body3"
                                                            fontWeight="bold"
                                                        >
                                                            Select Datasets
                                                        </Typography>
                                                        <Typography
                                                            variant="body3"
                                                            sx={{
                                                                color: (
                                                                    theme
                                                                ) =>
                                                                    theme
                                                                        .palette
                                                                        .disabled
                                                                        .onContainer,
                                                            }}
                                                        >
                                                            Subtext
                                                        </Typography>
                                                    </Grid>
                                                    <Grid
                                                        xs={12}
                                                        md={8}
                                                        item
                                                        container
                                                        gap={2}
                                                    >
                                                        {values.generateFiles.map(
                                                            (file, index) => (
                                                                <Stack
                                                                    key={index}
                                                                    sx={{
                                                                        padding: 4,
                                                                        color: (
                                                                            theme
                                                                        ) =>
                                                                            file.selected
                                                                                ? theme
                                                                                      .palette
                                                                                      .background
                                                                                      .onMain
                                                                                : theme
                                                                                      .palette
                                                                                      .disabled
                                                                                      .onContainer,
                                                                        border: (
                                                                            theme
                                                                        ) =>
                                                                            `1px solid ${theme.palette.outline}`,
                                                                        borderRadius: 1,
                                                                        width: "100%",
                                                                    }}
                                                                >
                                                                    <Stack
                                                                        flexDirection="row"
                                                                        gap={2}
                                                                        alignItems="center"
                                                                    >
                                                                        <Checkbox
                                                                            checked={
                                                                                file.selected
                                                                            }
                                                                            onChange={() => {
                                                                                const newFiles =
                                                                                    [
                                                                                        ...values.generateFiles,
                                                                                    ];
                                                                                newFiles[
                                                                                    index
                                                                                ].selected =
                                                                                    !newFiles[
                                                                                        index
                                                                                    ]
                                                                                        .selected;
                                                                                setValues(
                                                                                    {
                                                                                        ...values,
                                                                                        generateFiles:
                                                                                            newFiles,
                                                                                    }
                                                                                );
                                                                            }}
                                                                        />
                                                                        <ThumbnailAvatar
                                                                            color="surface"
                                                                            size="xs"
                                                                            sx={{
                                                                                borderRadius:
                                                                                    (
                                                                                        theme
                                                                                    ) =>
                                                                                        theme
                                                                                            .radius
                                                                                            .default,
                                                                                border: 1,
                                                                                borderColor:
                                                                                    (
                                                                                        theme
                                                                                    ) =>
                                                                                        theme
                                                                                            .palette
                                                                                            .stroke[100],
                                                                                [`& .${thumbnailAvatarClasses.image}`]:
                                                                                    {
                                                                                        backgroundColor:
                                                                                            (
                                                                                                theme
                                                                                            ) =>
                                                                                                theme
                                                                                                    .palette
                                                                                                    .background
                                                                                                    .main,
                                                                                    },
                                                                            }}
                                                                            src={
                                                                                logoMap[
                                                                                    CloudStorageType
                                                                                        .Azure
                                                                                ]
                                                                            }
                                                                            alt={
                                                                                CloudStorageType.Azure
                                                                            }
                                                                        />
                                                                        <Typography>
                                                                            {
                                                                                file.dataSourceName
                                                                            }
                                                                        </Typography>
                                                                    </Stack>
                                                                    <TextField
                                                                        key={
                                                                            file.fkDataSourceId
                                                                        }
                                                                        label="New source name"
                                                                        required
                                                                        fullWidth
                                                                        value={
                                                                            file.name
                                                                        }
                                                                        error={
                                                                            errors.generateFiles &&
                                                                            typeof errors.generateFiles[index] === "object" &&
                                                                            errors.generateFiles[index] !== null &&
                                                                            "name" in errors.generateFiles[index]
                                                                                ? Boolean((errors.generateFiles[index] as { name?: string }).name)
                                                                                : undefined
                                                                        }
                                                                        errorText={
                                                                            errors.generateFiles &&
                                                                            typeof errors.generateFiles[index] === "object" &&
                                                                            errors.generateFiles[index] !== null &&
                                                                            "name" in errors.generateFiles[index]
                                                                                ? (errors.generateFiles[index] as { name?: string }).name
                                                                                : undefined
                                                                        }
                                                                        onChange={(
                                                                            _,
                                                                            v
                                                                        ) => {
                                                                            const newFiles =
                                                                                [
                                                                                    ...values.generateFiles,
                                                                                ];
                                                                            newFiles[
                                                                                index
                                                                            ] =
                                                                                {
                                                                                    ...newFiles[
                                                                                        index
                                                                                    ],
                                                                                    name:
                                                                                        v ||
                                                                                        "",
                                                                                };
                                                                            setValues(
                                                                                {
                                                                                    ...values,
                                                                                    generateFiles:
                                                                                        newFiles,
                                                                                }
                                                                            );
                                                                            validateField(`generateFiles[${index}].name`);
                                                                        }}
                                                                    />
                                                                </Stack>
                                                            )
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            </Stack>
                                        }
                                        sidePanel={
                                            <Stack
                                                sx={{
                                                    padding: 4,
                                                    borderRadius: 2,
                                                    border: 1,
                                                    borderColor: (theme) =>
                                                        theme.palette
                                                            .stroke[100],
                                                    boxShadow:
                                                        "-2px -2px 18px 0px rgba(6, 11, 11, 0.04)",
                                                    rowGap: 4,
                                                    width: "100%",
                                                }}
                                            >
                                                <Stack>
                                                    <Typography
                                                        variant="body3"
                                                        fontWeight="bold"
                                                    >
                                                        Export Details
                                                    </Typography>
                                                    <Typography
                                                        variant="body3"
                                                        sx={{
                                                            color: (theme) =>
                                                                theme.palette
                                                                    .disabled
                                                                    .onContainer,
                                                        }}
                                                    >
                                                        {values.description ||
                                                            ""}
                                                    </Typography>

                                                    <Stack
                                                        sx={{
                                                            paddingY: 2,
                                                        }}
                                                    >
                                                        <Stack
                                                            flexDirection="row"
                                                            gap={2}
                                                            alignItems="center"
                                                        >
                                                            <AvatarGroup
                                                                max={3}
                                                                size="xxs"
                                                            >
                                                                {selectedDatasets.map(
                                                                    (sd, i) => {
                                                                        const cloudStorageType:
                                                                            | CloudStorageType
                                                                            | undefined =
                                                                            sd.cloudStorageType;
                                                                        return match(
                                                                            cloudStorageType
                                                                        )
                                                                            .with(
                                                                                CloudStorageType.Upload,
                                                                                () => {
                                                                                    return (
                                                                                        <IconAvatar
                                                                                            key={
                                                                                                i
                                                                                            }
                                                                                            square
                                                                                            size="sm"
                                                                                            color="background"
                                                                                            sx={{
                                                                                                border: 1,
                                                                                                borderColor:
                                                                                                    (
                                                                                                        theme
                                                                                                    ) =>
                                                                                                        theme
                                                                                                            .palette
                                                                                                            .stroke[100],
                                                                                            }}
                                                                                        >
                                                                                            <UploadOutline />
                                                                                        </IconAvatar>
                                                                                    );
                                                                                }
                                                                            )
                                                                            .otherwise(
                                                                                () => {
                                                                                    return (
                                                                                        <ThumbnailAvatar
                                                                                            key={
                                                                                                i
                                                                                            }
                                                                                            color="surface"
                                                                                            size="sm"
                                                                                            sx={{
                                                                                                borderRadius:
                                                                                                    (
                                                                                                        theme
                                                                                                    ) =>
                                                                                                        theme
                                                                                                            .radius
                                                                                                            .default,
                                                                                                border: 1,
                                                                                                borderColor:
                                                                                                    (
                                                                                                        theme
                                                                                                    ) =>
                                                                                                        theme
                                                                                                            .palette
                                                                                                            .stroke[100],
                                                                                                [`& .${thumbnailAvatarClasses.image}`]:
                                                                                                    {
                                                                                                        backgroundColor:
                                                                                                            (
                                                                                                                theme
                                                                                                            ) =>
                                                                                                                theme
                                                                                                                    .palette
                                                                                                                    .background
                                                                                                                    .main,
                                                                                                    },
                                                                                            }}
                                                                                            src={
                                                                                                cloudStorageType &&
                                                                                                cloudStorageType !==
                                                                                                    CloudStorageType.Upload
                                                                                                    ? logoMap[
                                                                                                          cloudStorageType
                                                                                                      ]
                                                                                                    : undefined
                                                                                            }
                                                                                            alt={
                                                                                                sd.cloudStorageType
                                                                                            }
                                                                                        />
                                                                                    );
                                                                                }
                                                                            );
                                                                    }
                                                                )}
                                                            </AvatarGroup>
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight="semibold"
                                                            >
                                                                {
                                                                    datasets[0]
                                                                        ?.dataSource
                                                                        ?.name
                                                                }
                                                            </Typography>
                                                        </Stack>
                                                    </Stack>
                                                </Stack>
                                                <Divider />
                                                <Stack>
                                                    <Typography
                                                        variant="body3"
                                                        fontWeight="bold"
                                                    >
                                                        Files
                                                    </Typography>
                                                    <Typography
                                                        variant="body3"
                                                        sx={{
                                                            color: (theme) =>
                                                                theme.palette
                                                                    .disabled
                                                                    .onContainer,
                                                        }}
                                                    >
                                                        These files will be
                                                        generated
                                                    </Typography>
                                                    {values.generateFiles
                                                        .filter(
                                                            (f) => f.selected
                                                        )
                                                        .map((file, index) => (
                                                            <Stack
                                                                key={index}
                                                                sx={{
                                                                    paddingY: 2,
                                                                }}
                                                            >
                                                                <Tag
                                                                    density="dense"
                                                                    variant="tonal"
                                                                    sx={{
                                                                        backgroundColor:
                                                                            "transparent",
                                                                    }}
                                                                    startNode={
                                                                        <ThumbnailAvatar
                                                                            color="surface"
                                                                            size="sm"
                                                                            src="https://sabiggeomedia01.blob.core.windows.net/media/Files.png"
                                                                            alt={
                                                                                "files icon"
                                                                            }
                                                                        />
                                                                    }
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        fontWeight="semibold"
                                                                    >
                                                                        {convertToParquetFileName(
                                                                            file.name
                                                                        )}
                                                                    </Typography>
                                                                </Tag>
                                                            </Stack>
                                                        ))}
                                                </Stack>
                                            </Stack>
                                        }
                                        breadcrumbs={[
                                            {
                                                label: "Map Template",
                                                onClick: () => onClose(),
                                            },
                                            { label: "Generate File" },
                                        ]}
                                        onClickGenerate={() => handleSubmit()}
                                    />
                                </Stack>
                            </Stack>
                        );
                    }}
                </Formik>
            </FlexScrollArea>
        </DatascapeMainLayout>
    );
};

export default GenerateFile;
